import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { FeatureGate } from "../components/feature-gate";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import type { Product } from "../api";
import type { MovementDocRef } from "../components/product-movements-tab";
import { TovarsPage } from "./TovarsPage";
import { XaridlarPage } from "./XaridlarPage";
import { SotuvlarPage } from "./SotuvlarPage";
import { QaytarishlarPage } from "./QaytarishlarPage";
import { ChiqimPage } from "./ChiqimPage";
import { JarayondaPage } from "./JarayondaPage";
import { YetkazibBeruvchilarPage } from "./YetkazibBeruvchilarPage";

export type OmborTab =
  | "tovarlar"
  | "xaridlar"
  | "sotuvlar"
  | "qaytarishlar"
  | "chiqim"
  | "jarayonda"
  | "yetkazib";

/**
 * Tahlil > Signallar qatoridagi amal — Ombor mos tab + payload bilan ochiladi.
 * Ombor sahifasi Tahlil ochiq turganda unmount holatda, shuning uchun payload
 * mount paytida `useState` boshlang'ich qiymati sifatida BIR marta iste'mol
 * qilinadi (seq kerak emas); eskirishi `App.handleSetTab` da tozalanadi.
 */
export type OmborJump =
  | { kind: "xarid"; product: Product } // tugayotgan → xarid formasi
  | { kind: "chiqim"; product: Product } // qotgan → hisobdan chiqarish
  | { kind: "purchaseDoc"; docId: number }; // qotgan/sekin → oxirgi xarid hujjati

/**
 * Ombor — tovar katalogi (Tovarlar), ko'p qatorli Xaridlar va Sotuvlar
 * hujjatlari, Qaytarishlar, Hisobdan chiqarish, Jarayondagi bandliklar va
 * Yetkazib beruvchilar bilan hisob-kitob bitta mental obyektga birlashtirilgan.
 * Hammasi `tovars` feature'iga bog'liq — comfort tarifda butun bo'lim
 * ko'rinmaydi. Tovar qatoridan "Xarid"/"Sotuv" bosilsa, mos tab ochiladi va
 * o'sha tovar oldindan tanlangan holda forma chiqadi. Ombor signallari Tahlil
 * sahifasida yashaydi; u yerdagi qator amallari `jump` orqali shu yerga tushadi.
 */
export function OmborPage({
  initialTab = "tovarlar",
  jump = null,
  onOpenTariff,
  onOpenWork,
  onTabChange,
}: {
  initialTab?: OmborTab;
  /** Tahlil > Signallar'dan kelgan amal — mount paytida bir marta iste'mol qilinadi. */
  jump?: OmborJump | null;
  onOpenTariff: () => void;
  /** Ishga biriktirilgan tovar satri — Ombordan tashqariga, Ishlar sahifasiga. */
  onOpenWork: (workId: number) => void;
  onTabChange?: (tab: OmborTab) => void;
}) {
  return (
    <FeatureGate feature="tovars" requiredTariff="pro" onUpgrade={onOpenTariff}>
      <OmborInner initialTab={initialTab} jump={jump} onOpenWork={onOpenWork} onTabChange={onTabChange} />
    </FeatureGate>
  );
}

function OmborInner({
  initialTab,
  jump,
  onOpenWork,
  onTabChange,
}: {
  initialTab: OmborTab;
  jump: OmborJump | null;
  onOpenWork: (workId: number) => void;
  onTabChange?: (tab: OmborTab) => void;
}) {
  const { t } = useTranslation("ombor");
  const [tab, setTab] = useState<OmborTab>(
    jump ? (jump.kind === "chiqim" ? "chiqim" : "xaridlar") : initialTab,
  );
  // Tovardan boshlangan xarid/sotuv uchun tanlangan tovar (bir martalik).
  const [xaridProduct, setXaridProduct] = useState<Product | null>(
    jump?.kind === "xarid" ? jump.product : null,
  );
  const [sotuvProduct, setSotuvProduct] = useState<Product | null>(null);
  // Signaldan (Tahlil'dan jump orqali) boshlangan hisobdan chiqarish — mount'da
  // bir marta o'rnatiladi, sahifa ichida uni o'zgartiradigan yo'l yo'q.
  const chiqimProduct =
    jump?.kind === "chiqim" ? { product: jump.product, seq: 1 } : null;
  // "Yangi xarid"/"Yangi sotuv"/"Yangi bandlik" tugmalari tablar qatorida —
  // bosilganda mos sahifaga signal yuboramiz (forma ochiladi).
  const [xaridOpen, setXaridOpen] = useState(0);
  const [sotuvOpen, setSotuvOpen] = useState(0);
  const [jarayondaOpen, setJarayondaOpen] = useState(0);
  const [chiqimOpen, setChiqimOpen] = useState(0);
  // Tovar jurnalidan hujjatga o'tish. `seq` — bir xil hujjat qayta bosilsa ham
  // effekt qayta ishga tushsin (faqat id bo'lsa ikkinchi bosish sezilmasdi).
  // Yetkazib beruvchiga qaytarish hujjatdan boshlanadi (API `original_document_id`
  // talab qiladi), shuning uchun `purchaseDoc` jump'i shu fokus yo'lidan boradi.
  const [focus, setFocus] = useState<{
    tab: OmborTab;
    docId: number;
    seq: number;
  } | null>(
    jump?.kind === "purchaseDoc"
      ? { tab: "xaridlar", docId: jump.docId, seq: 1 }
      : null,
  );

  const selectTab = (next: OmborTab) => {
    setTab(next);
    onTabChange?.(next);
  };

  /** Harakat satridagi hujjat havolasi — mos tabga (yoki Ishlarga) yo'naltiradi. */
  function openDoc(ref: MovementDocRef) {
    if (ref.kind === "work") {
      onOpenWork(ref.id);
      return;
    }
    const target: OmborTab =
      ref.kind === "purchase"
        ? "xaridlar"
        : ref.kind === "sale"
          ? "sotuvlar"
          : ref.kind === "writeoff"
            ? "chiqim"
            : "qaytarishlar";
    setFocus((prev) => ({
      tab: target,
      docId: ref.id,
      seq: (prev?.seq ?? 0) + 1,
    }));
    selectTab(target);
  }

  /** Hujjat FAQAT o'z tabiga uzatiladi — boshqa tab uni ochib yubormasin. */
  const focusFor = (target: OmborTab) =>
    focus && focus.tab === target ? { id: focus.docId, seq: focus.seq } : null;

  return (
    <Tabs
      value={tab}
      // Tab kontenti almashganda unmount bo'ladi (`TabsContent` null qaytaradi),
      // shuning uchun fokus tozalanmasa foydalanuvchi o'zi qaytib kelganda eski
      // hujjat yana ajratib ko'rsatilardi. Jurnaldan o'tish `openDoc` orqali
      // boradi va u fokusni tabdan KEYIN o'rnatadi.
      onValueChange={(v) => {
        setFocus(null);
        selectTab(v as OmborTab);
      }}
      className="warehouse-page space-y-4"
    >
      <div className="warehouse-toolbar flex items-center justify-between gap-3">
        <TabsList>
          <TabsTrigger value="tovarlar">{t("ombor.tabs.tovarlar")}</TabsTrigger>
          <TabsTrigger value="xaridlar">{t("ombor.tabs.xaridlar")}</TabsTrigger>
          <TabsTrigger value="sotuvlar">{t("ombor.tabs.sotuvlar")}</TabsTrigger>
          <TabsTrigger value="qaytarishlar">{t("ombor.tabs.qaytarishlar")}</TabsTrigger>
          <TabsTrigger value="chiqim">{t("ombor.tabs.chiqim")}</TabsTrigger>
          <TabsTrigger value="jarayonda">{t("ombor.tabs.jarayonda")}</TabsTrigger>
          <TabsTrigger value="yetkazib">{t("ombor.tabs.yetkazib")}</TabsTrigger>
        </TabsList>
        {tab === "xaridlar" && (
          <Button size="sm" onClick={() => setXaridOpen((x) => x + 1)}>
            <Plus />
            {t("xaridlar.newPurchase")}
          </Button>
        )}
        {tab === "sotuvlar" && (
          <Button size="sm" onClick={() => setSotuvOpen((x) => x + 1)}>
            <Plus />
            {t("sotuvlar.newSale")}
          </Button>
        )}
        {tab === "jarayonda" && (
          <Button size="sm" onClick={() => setJarayondaOpen((x) => x + 1)}>
            <Plus />
            {t("jarayonda.new")}
          </Button>
        )}
        {tab === "chiqim" && (
          <Button size="sm" onClick={() => setChiqimOpen((x) => x + 1)}>
            <Plus />
            {t("chiqim.new")}
          </Button>
        )}
      </div>

      <TabsContent value="tovarlar" className="mt-0">
        <TovarsPage
          onXarid={(p) => {
            setXaridProduct(p);
            selectTab("xaridlar");
          }}
          onSotuv={(p) => {
            setSotuvProduct(p);
            selectTab("sotuvlar");
          }}
          onOpenDoc={openDoc}
        />
      </TabsContent>
      <TabsContent value="xaridlar" className="mt-0">
        <XaridlarPage
          initialProduct={xaridProduct}
          openSignal={xaridOpen}
          focusDoc={focusFor("xaridlar")}
        />
      </TabsContent>
      <TabsContent value="sotuvlar" className="mt-0">
        <SotuvlarPage
          initialProduct={sotuvProduct}
          openSignal={sotuvOpen}
          focusDoc={focusFor("sotuvlar")}
        />
      </TabsContent>
      <TabsContent value="qaytarishlar" className="mt-0">
        <QaytarishlarPage focusDoc={focusFor("qaytarishlar")} />
      </TabsContent>
      <TabsContent value="chiqim" className="mt-0">
        <ChiqimPage
          openSignal={chiqimOpen}
          initialProduct={chiqimProduct}
          focusDoc={focusFor("chiqim")}
        />
      </TabsContent>
      <TabsContent value="jarayonda" className="mt-0">
        <JarayondaPage openSignal={jarayondaOpen} />
      </TabsContent>
      <TabsContent value="yetkazib" className="mt-0">
        <YetkazibBeruvchilarPage />
      </TabsContent>
    </Tabs>
  );
}
